import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import {
  Caption,
  Message,
  Spacer,
  Spinner,
  Stack,
  mq
} from '@kvdbil/components';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import BiddingTimer from './components/BiddingTimer';
import BiddingMessage from './components/BiddingMessage';
import {
  memberCanBidSelector,
  isAuthenticated as isAuthenticatedSelector,
  session as sessionSelector,
  isLoggedInSelector,
  hasForeignConnectionsSelector
} from '~/App/shared/selectors/sessionSelector';
import { isDisconnectedSelector } from '~/App/shared/selectors/connectionSelector';
import BidForm from './components/BidForm';
import ToolTip from './components/Tooltip';
import BiddingHistoryContainer from './components/BiddingHistoryContainer';
import BiddingHeader from './components/BiddingHeader';
import { useTranslation } from '~/Locale';
import {
  closeBiddingView,
  updateMyBidderAlias
} from '~/App/shared/actions/objectViewDrawerActions';
import { MaxBid, Bid } from '~/App/shared/interfaces/Bid';
import { ProcessedAuctionStates } from '~/helpers/auction';
import { handleErrorPlacingBid } from '~/helpers/bidding';
import { reportPurchase } from '~/helpers/emarsys';
import { requestErrorHandler } from '~/helpers/notifyError';
import Auction from '~/App/shared/interfaces/Auction';
import { editMaxBid, placeBid } from '~/helpers/orchestration/auction';
import { ReduxDispatch } from '~/App/shared/interfaces/Redux';
import { useGA4UserIsHydrated } from '~/App/shared/hooks/useGA4User';
import {
  objectPropertiesFromAuction,
  trackGA4BidPlaced
} from '~/helpers/client/ga4TrackEvent';
import AuctionTips from './components/AuctionTips';
import Restrictions from '../Restrictions';
import { getRestrictionsInfo } from '../helpers';
import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import { getAuction } from '~/App/shared/actions/auction';
import StatusPageContainer from '~/App/shared/components/StatusPageContainer';
import useShowExclVat from '~/App/shared/hooks/useShowExclVat';
import { useAccountVerificationStatus } from './hooks/useAccountVerificationStatus';
import BiddingViewAuthAndVerification from './components/BiddingViewAuth/BiddingViewAuthAndVerification';
import { useObjectPageCMSData } from '~/App/views/ObjectPage/hooks';
import { LoanWarning } from '~/App/views/Checkout/components/Configure/LoanWarning';
import { exclVATtoInclVAT } from '~/helpers/currency';

dayjs.extend(relativeTime);

const BiddingError = styled(Message).attrs({ type: 'error', fullWidth: true })`
  margin: 0.75rem 0;
  border-radius: 0;
`;

interface BiddingActionProps {
  isCounting: boolean;
  isLeading: boolean;
  isParticipating: boolean;
  isBiddingLoginOpen?: boolean;
}
const BiddingAction = styled.div<BiddingActionProps>`
  ${({ isCounting, isParticipating, isBiddingLoginOpen }) => {
    if (isCounting && !isBiddingLoginOpen) {
      return css`
        border-radius: 5px;
        width: 100%;
        padding: 0 0 2em;
        margin-top: 90px;

        ${mq('tablet')} {
          margin-top: ${isParticipating ? '2rem' : '90px'};
        }
      `;
    }
  }}
`;

const ReservationPriceInformation = styled.div`
  margin-top: 2rem;
`;

const MaxBidForm = styled.div`
  display: ${({ hidden }) => (hidden ? 'none' : 'block')};
`;

const BiddingBidboxWarning = styled.div`
  color: ${({ theme }) => theme.colors.error.dark};
  padding: 0.5em 0 0;
  text-align: center;
  font-weight: 500;
`;

const AuthAndBidFormWrapper = styled(Stack)`
  margin-top: 2rem;
`;

interface PlaceBidResponse {
  data: {
    maxBid: MaxBid;
    myBidderAlias: string;
  };
}
export interface BiddingProps {
  auction: ProcessedAuction;
  auctionStates: ProcessedAuctionStates;
}
const Bidding = ({ auction, auctionStates }: BiddingProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<ReduxDispatch>();
  const showExclVat = useShowExclVat(auction?.processObject);

  const session = useSelector(sessionSelector);
  const isDisconnected = useSelector(isDisconnectedSelector);
  const canPlaceBid = useSelector(memberCanBidSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const isLoggedIn = useSelector(isLoggedInSelector);
  const hasForeignConnections = useSelector(hasForeignConnectionsSelector);

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [myMaxBid, setMyMaxBid] = useState<Bid | MaxBid | null>();
  const [auctionExpired, setAuctionExpired] = useState(false);
  const [authInUse, setAuthInUse] = useState(false);

  const { ga4User } = useGA4UserIsHydrated();

  const { accountVerificationStatus } = useAccountVerificationStatus();
  const isAuthAndVerificationComplete = !accountVerificationStatus;
  const { data: cmsData } = useObjectPageCMSData();

  const vatExcludedBiddingAllowed =
    auction.activeAuction?.vatExcludedBiddingAllowed ?? false;
  const lowestBidToPlace =
    auction.activeAuction?.lowestAllowedMaxBidAmount ?? 0;

  const refetchAuction = useCallback(async () => {
    if (!auction.id) {
      return null;
    }
    try {
      const action = await dispatch(getAuction(auction.id));
      // if auction does not exist, the backend will response with AuctionNotFound schema
      const newAuction = action.payload as Auction;
      if (action.type === 'GET_AUCTION_SUCCESS' && newAuction.id) {
        const myMaxBid = newAuction.activeAuction?.myMaxBid;
        if (myMaxBid) {
          setMyMaxBid(myMaxBid);
        }
      }

      return newAuction;
    } catch (err: unknown) {
      requestErrorHandler(err);
    }
  }, [auction.id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(closeBiddingView());
    };
  }, [dispatch]);

  useEffect(() => {
    if (auction.id) {
      void refetchAuction();
    }
  }, [isAuthenticated, refetchAuction, auction.id]);

  const handleConfirmBid = (bidAmountFormValue: number) => {
    const bidAmount = vatExcludedBiddingAllowed
      ? exclVATtoInclVAT(bidAmountFormValue)
      : bidAmountFormValue;
    const isLeading = auctionStates.isLeading;
    const isParticipating = auctionStates.isParticipating;
    setError('');

    const maxBidId = myMaxBid?.id || -1;
    const bidPromise = !isLeading
      ? placeBid({
          amount: bidAmount,
          auctionId: auction.id
        }).then((response: PlaceBidResponse) => {
          return response;
        })
      : editMaxBid({
          maxBidId,
          amount: bidAmount,
          auctionId: auction.id
        });

    setIsLoading(true);

    bidPromise
      .then(async ({ data }: PlaceBidResponse) => {
        const maxBidId = data?.maxBid?.id;

        setMyMaxBid(data.maxBid);

        if (data.myBidderAlias) {
          dispatch(
            updateMyBidderAlias({
              auctionId: auction.id,
              myBidderAlias: data.myBidderAlias
            })
          );
        }

        if (auction.id) {
          const newAuction = await refetchAuction();

          if (!isLeading && newAuction) {
            const userBidCount =
              newAuction.activeAuction?.bids?.filter(
                bid =>
                  bid?.bidderAlias === newAuction?.activeAuction?.myBidderAlias
              )?.length ?? 0;

            if (userBidCount > 0) {
              trackGA4BidPlaced(
                {
                  object: objectPropertiesFromAuction(newAuction),
                  nth_bid: userBidCount
                },
                ga4User
              );
            }
          }
        }

        // report purchase event only for first bid per user
        if (!isParticipating) {
          const memberId = session?.member?.id;
          reportPurchase(maxBidId.toString(), auction.id, bidAmount, memberId);
        }
      })
      .catch((error: Error) => {
        setError(
          handleErrorPlacingBid(error, {
            t,
            maxAllowedBid: auction.maxAllowedBid ?? 0,
            bidAmount,
            minBidRaise: auction.minBidRaise
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
        setIsConfirmationOpen(prev => !prev);
      });
  };

  const preliminaryCloseAt = auction.activeAuction?.preliminaryCloseAt ?? '';

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        dayjs(preliminaryCloseAt).add(10, 'second').toDate() < new Date() &&
        auction.state === 'OPEN'
      ) {
        setAuctionExpired(true);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [setAuctionExpired, preliminaryCloseAt, auction.state]);

  // Keep this component mounted but don't show anything.
  if (isLoading) {
    return (
      <>
        <Spacer height={15} />
        <StatusPageContainer>
          <Spinner color="neutral" speed="fast" size="large" />
        </StatusPageContainer>
      </>
    );
  }

  const {
    isLeading,
    isCounting,
    isClosed,
    isParticipating,
    isWinner,
    isSoldByBuyNow
  } = auctionStates;

  const { member } = session;
  const memberType = member.memberType;

  const isCheckoutComplete = auction.checkoutOrder?.state === 'COMPLETE';
  const checkoutOrderId = auction.checkoutOrder?.orderId;

  const restrictionsInfo = getRestrictionsInfo(
    member,
    false,
    auction,
    hasForeignConnections,
    t
  );

  const isReservationPriceReached =
    auction.activeAuction?.reservationPriceReached ||
    Boolean(auction.winningBid);

  const winningLink = `/ROUTES.CHECKOUT/ROUTES.CHECKOUT_START/${auction.id}/AUCTION`;
  const maxBidAmount = myMaxBid?.amount ?? 0;

  const releasing = auction?.releasing;

  const { leadingBid } = auction;
  const leadingBidAmount = leadingBid?.amount;

  const biddingFeeAmount =
    memberType === 'PERSON' ? auction.biddingFee : auction.biddingFeeCorporate;
  const parsedBiddingFeeAmount = parseInt(biddingFeeAmount);

  const showRestrictions =
    restrictionsInfo?.showRestrictionsBox &&
    isAuthAndVerificationComplete &&
    !authInUse;

  const hasBids = Boolean(auction.bids.length > 0);

  return (
    <div translate="no">
      {!authInUse && (
        <BiddingHeader
          isClosed={isClosed}
          isCounting={isCounting}
          isLeading={isLeading}
          isParticipating={isParticipating}
          preliminaryCloseAt={preliminaryCloseAt}
        />
      )}

      {!isDisconnected && (
        <>
          {!authInUse && isAuthenticated && !isNaN(parsedBiddingFeeAmount) && (
            <AuctionTips
              feeAmount={parsedBiddingFeeAmount}
              isBiddingObject
              hasDeductibleVat={auction.processObject.deductibleVat}
            />
          )}

          {auctionExpired && (
            <BiddingError>
              {t(
                'Your browser seems to have lost connection to our site. Please refresh the page to reconnect.'
              )}
            </BiddingError>
          )}

          {!isClosed && (
            <BiddingAction
              data-testid="bidding-action"
              isCounting={isCounting}
              isLeading={isLeading}
              isParticipating={isParticipating}
              isBiddingLoginOpen={authInUse}
            >
              {isCounting && !authInUse && (
                <>
                  <BiddingTimer
                    closeAt={preliminaryCloseAt}
                    isLeading={isLeading}
                    countdownDuration={auction.countdownDuration}
                    isParticipating={isParticipating}
                    auctionId={auction.id}
                  />
                </>
              )}

              <AuthAndBidFormWrapper spacing={2}>
                <BiddingViewAuthAndVerification
                  setAuthInUse={setAuthInUse}
                  isBiddingObject
                />

                {showRestrictions && isLoggedIn && (
                  <Restrictions text={restrictionsInfo?.text} />
                )}
                {!authInUse && (
                  <MaxBidForm data-testid="max-bid-form">
                    <BidForm
                      error={error}
                      minBidRaise={auction.minBidRaise}
                      lowestBidToPlace={lowestBidToPlace}
                      leadingBidAmount={leadingBidAmount}
                      isLoading={isLoading}
                      isDisabled={
                        !isAuthAndVerificationComplete ||
                        !canPlaceBid ||
                        restrictionsInfo.showRestrictionsBox
                      }
                      onPlaceBid={bidAmount => handleConfirmBid(bidAmount)}
                      isConfirmationOpen={isConfirmationOpen}
                      showExclVat={showExclVat}
                      vatExcludedBiddingAllowed={vatExcludedBiddingAllowed}
                    />
                  </MaxBidForm>
                )}
              </AuthAndBidFormWrapper>
            </BiddingAction>
          )}
        </>
      )}
      {isClosed && !isSoldByBuyNow && (
        <BiddingMessage
          isWinner={isWinner}
          leadingBidAmount={leadingBidAmount ?? 0}
          winningLink={winningLink}
          hasCheckoutOrder={Boolean(auction.checkoutOrder)}
          checkoutOrderId={checkoutOrderId}
          isCheckoutComplete={isCheckoutComplete}
          releasingCost={releasing?.monthlyCost ?? 0}
          isParticipating={isParticipating}
          reservationPriceReached={isReservationPriceReached}
          hasBids={hasBids}
          vatExcludedBiddingAllowed={vatExcludedBiddingAllowed}
        />
      )}
      {isDisconnected && !isClosed && (
        <div>
          <div>
            <BiddingError withIcon>
              {t('You have lost connection! Try reloading.')}
            </BiddingError>
          </div>
          {isLeading && (
            <BiddingBidboxWarning>
              {t('We can no longer guarantee that you have the leading bid.')}
            </BiddingBidboxWarning>
          )}
        </div>
      )}
      {!authInUse && !isClosed && (
        <ReservationPriceInformation>
          <ToolTip
            centered
            label={
              isReservationPriceReached
                ? t('Resprice reached')
                : t('Resprice not reached')
            }
            content={<Caption>{t('Reservation price explanation')}</Caption>}
          />
        </ReservationPriceInformation>
      )}
      {!authInUse && (
        <BiddingHistoryContainer
          auction={auction}
          isClosed={isClosed}
          isLeading={isLeading}
          isReservationPriceReached={isReservationPriceReached}
          maxBidAmount={maxBidAmount}
        />
      )}

      {!authInUse && cmsData?.buyMethods?.loanWarning && (
        <LoanWarning content={cmsData?.buyMethods?.loanWarning} />
      )}
    </div>
  );
};

export default Bidding;
